import { inject, Injectable } from "@angular/core";
import { Utils } from "@bitwarden/common/misc/utils";
import { EncArrayBuffer } from "@bitwarden/common/models/domain/enc-array-buffer";
import { SymmetricCryptoKey } from "@bitwarden/common/models/domain/symmetric-crypto-key";
import { GlobalService } from "@bitwarden/common/services/global/global.service";
import { VaultFileView } from "@bitwarden/web-vault/app/models/view/vault-file/vault-file.view";
import { DataRepositoryService } from "@bitwarden/web-vault/app/services/DataRepository/data-repository.service";
import { FileEncryptionService } from "@bitwarden/web-vault/app/services/DataService/file-upload/file-encryption.service";
import {
  BalanceActionEnum,
  BookBalance,
} from "@bitwarden/web-vault/app/shared/utils/helper.account/balance";
import { GlossApiProxyRequest } from "@bitwarden/web-vault/app/shared/utils/helper.glos-api-call/gloss-api-proxy-request";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import {
  CabinetFileType,
  CabinetFileUploadedData,
  VaultFileValidationType,
} from "../../../models/types/cabinet.types";
import { CabinetFileError, logCabinetFileError, toCabinetFileError } from "./cabinet-file-error";

@Injectable({
  providedIn: "root",
})
export class CabinetService {
  constructor(
    private globalService: GlobalService,
    private dataRepositoryService: DataRepositoryService,
    private fileEncryption: FileEncryptionService,
  ) {}

  private userStore: UserStoreService = inject(UserStoreService);

  displayCatchError(error: unknown) {
    const fileError = toCabinetFileError("upload", error, "fileUploadFailed");
    logCabinetFileError(fileError);
    this.globalService.showErrorMessage("errorOccurred", fileError.messageKey);
  }

  async saveFile(data: CabinetFileUploadedData) {
    const fileValidation = this.validateFile(data);
    if (!fileValidation.isValid) {
      this.globalService.showWarningMessage("warning", fileValidation.messageKey);
      return false;
    }

    await this.prepareBalances(data);

    const { key, encArrayBuffer } = await this.fileEncryption.encrypt(data.file);

    const newlySavedVaultFile = await this.saveFileToDali(data, key);
    if (!newlySavedVaultFile) {
      this.globalService.showErrorMessage("errorOccurred", "fileUploadFailed");
      return false;
    }

    try {
      await this.saveContentToServer(newlySavedVaultFile, data.file, encArrayBuffer);
    } catch (e) {
      // The metadata is saved before the content, so without this the Cabinet list keeps an
      // entry whose content never arrived and that fails every time it's opened.
      await this.rollbackSavedFile(newlySavedVaultFile, "upload error", false);
      this.displayCatchError(e);
      return false;
    }

    // here is where we add the balances
    const balancesSaved = await this.addBalances(data);
    if (!balancesSaved) {
      // Roll back the just-saved file so a retry doesn't create a duplicate
      // that has no opening/closing balances.
      await this.rollbackSavedFile(newlySavedVaultFile, "balance save error", true);
      this.globalService.showErrorMessage("errorOccurred", "somethingWentWrong");
      return false;
    }
    this.globalService.showSuccessMessage("success", "fileUploadedSuccessfully");

    return true;
  }

  /**
   * Best-effort removal of a just-saved file whose save didn't complete; keeps the original error.
   *
   * The remote delete often fails for the same reason the upload did (expired session, API
   * unreachable). If the upload failed, drop the local metadata anyway - at worst that orphans an
   * object S3 got without confirming, while keeping it leaves an entry that fails every time it's opened.
   * If the content was uploaded, keep the entry so the stored file stays reachable.
   */
  private async rollbackSavedFile(
    vaultFile: VaultFileView,
    reason: string,
    contentUploaded: boolean,
  ): Promise<void> {
    try {
      await this.deleteFile(vaultFile, true);
      return;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`Failed to roll back cabinet file after ${reason}`, e);
    }
    if (contentUploaded) {
      return;
    }
    try {
      await this.userStore.vaultFiles.delete(vaultFile);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`Failed to remove local cabinet file after ${reason}`, e);
    }
  }

  private async prepareBalances(data: CabinetFileUploadedData): Promise<void> {
    // Resolve each balance (isReady triggers the update/keep conflict dialog). If the
    // user declines a conflict the action stays require_user; we do NOT abort the upload
    // for that — the file (and any resolved balances) still save, and the unresolved
    // balance is skipped in addBalances. Best-effort, like the PDF-upload flow.
    for (const balance of this.getStatementBalances(data)) {
      await balance.isReady();
    }
  }

  private async addBalances(data: CabinetFileUploadedData): Promise<boolean> {
    for (const accountView of data.statementAccounts) {
      const openingBalance = data.openingBalances.get(accountView.id);
      const closingBalance = data.closingBalances.get(accountView.id);

      // Every linked account is expected to carry both balances (the wizard requires them). A
      // missing entry means it was dropped upstream (e.g. never resolved to a BookBalance), so
      // fail rather than silently skipping and reporting a false success to the rollback check.
      if (!openingBalance || !closingBalance) {
        return false;
      }

      if (!(await this.savePreparedOpeningBalance(openingBalance))) {
        return false;
      }

      if (!(await this.savePreparedClosingBalance(closingBalance))) {
        return false;
      }
    }

    return true;
  }

  private getStatementBalances(data: CabinetFileUploadedData): BookBalance[] {
    const balances: BookBalance[] = [];
    for (const accountView of data.statementAccounts) {
      const openingBalance = data.openingBalances.get(accountView.id);
      const closingBalance = data.closingBalances.get(accountView.id);
      if (openingBalance) {
        balances.push(openingBalance);
      }
      if (closingBalance) {
        balances.push(closingBalance);
      }
    }
    return balances;
  }

  private async savePreparedOpeningBalance(balance: BookBalance): Promise<boolean> {
    // keep_existing: nothing to persist. require_user/null: the user declined the
    // conflict or it stayed unresolved — skip it best-effort so the file still saves.
    if (
      balance.action === BalanceActionEnum.keep_existing ||
      balance.action === BalanceActionEnum.require_user ||
      balance.action === null
    ) {
      return true;
    }
    if (balance.action === BalanceActionEnum.add) {
      return await balance.addOpeningBalance();
    }
    if (balance.action === BalanceActionEnum.edit) {
      return await balance.updateConfirmedBalance();
    }
    return false;
  }

  private async savePreparedClosingBalance(balance: BookBalance): Promise<boolean> {
    // keep_existing: nothing to persist. require_user/null: the user declined the
    // conflict or it stayed unresolved — skip it best-effort so the file still saves.
    if (
      balance.action === BalanceActionEnum.keep_existing ||
      balance.action === BalanceActionEnum.require_user ||
      balance.action === null
    ) {
      return true;
    }
    if (balance.action === BalanceActionEnum.add) {
      return await balance.addClosingBalance();
    }
    if (balance.action === BalanceActionEnum.edit) {
      return await balance.updateConfirmedBalance();
    }
    return false;
  }

  private async saveFileToDali(data: CabinetFileUploadedData, key: SymmetricCryptoKey) {
    try {
      const newVaultFile = this.generateVaultFile(data, key);
      const saveFileResult = await this.userStore.vaultFiles.save(newVaultFile);
      return saveFileResult ? newVaultFile : null;
      // return await this.vaultFileService.create(newVaultFile);
    } catch (error) {
      // saveFile shows the toast for a null return; showing one here too doubled it.
      // eslint-disable-next-line no-console
      console.error("[Cabinet] Saving the vault file metadata failed", error);
      return null;
    }
  }

  validateFile(data: CabinetFileUploadedData): VaultFileValidationType {
    if (!data.file) {
      return { isValid: false, messageKey: "emptyFileErrorMessage" };
    }

    if (!data.name) {
      return { isValid: false, messageKey: "fileUploadNameRequired" };
    }
    if (!data.mimeType) {
      return { isValid: false, messageKey: "fileTypeNotSupported" };
    }

    if (!data.statementToDate) {
      return { isValid: false, messageKey: "fileUploadStatementToDateRequired" };
    }
    if (!data.statementFromDate) {
      return { isValid: false, messageKey: "fileUploadStatementFromDateRequired" };
    }
    if (!data.statementAccounts || data.statementAccounts.length === 0) {
      return { isValid: false, messageKey: "fileUploadStatementAccountsRequired" };
    }

    // Statements without a running-balance column don't auto-fill balances, so the
    // maps can be empty. Catch it here with an actionable message instead of failing
    // deep in generateVaultFile / addBalances with a generic "something went wrong".
    const missingBalance = data.statementAccounts.some(
      (acc) => !data.openingBalances?.get(acc.id) || !data.closingBalances?.get(acc.id),
    );
    if (missingBalance) {
      return { isValid: false, messageKey: "fileUploadBalancesRequired" };
    }

    return { isValid: true, messageKey: "" };
  }

  private generateVaultFile(data: CabinetFileUploadedData, key: SymmetricCryptoKey): VaultFileView {
    const newVaultFileView = new VaultFileView();

    newVaultFileView.name = data.name;
    newVaultFileView.mimeType = data.mimeType;
    newVaultFileView.statementToDate = data.statementToDate;
    newVaultFileView.statementFromDate = data.statementFromDate;
    // Presence-guard the first map entry: an empty map (statement with no balance
    // column, no manual balances entered) otherwise throws on `.next().value.balanceForm`.
    // validateFile blocks this case earlier, but keep the deref safe regardless.
    // balanceForm.balance is a string (e.g. "100"); openingBalances/closingBalances
    // are numbers (and persist to VaultFileStoreModel.ob/cb: number). Coerce so a
    // string is not stored in a number field (#3930). Left nullish when absent.
    const openingBalance = data.openingBalances.values().next().value?.balanceForm?.balance;
    const closingBalance = data.closingBalances.values().next().value?.balanceForm?.balance;
    newVaultFileView.openingBalances =
      openingBalance == null ? openingBalance : Number(openingBalance);
    newVaultFileView.closingBalances =
      closingBalance == null ? closingBalance : Number(closingBalance);
    newVaultFileView.encryptedKey = key;
    newVaultFileView.statementAccounts = data.statementAccounts;
    newVaultFileView.accountIds = data.statementAccounts.map((sAccount) => sAccount.id);
    newVaultFileView.fileSize = data.file.size;

    return newVaultFileView;
  }

  /** Uploads the encrypted content. Resolves true, or throws a CabinetFileError saying which step
   * failed and why (status / gloss-api error code). */
  private async saveContentToServer(
    vaultFile: VaultFileView,
    file: File,
    encArrayBuffer: EncArrayBuffer,
  ): Promise<boolean> {
    if (!file) {
      return true;
    }

    const bufferText = Utils.fromBufferToB64(encArrayBuffer.buffer);
    const encryptedBlob = new Blob([bufferText], { type: "text/plain" });

    const presignRequest = new GlossApiProxyRequest({
      endpoint: "presignUploadFile",
      filename: vaultFile.id,
      fileSize: encryptedBlob.size,
    });
    let presignResponse;
    try {
      presignResponse = await this.dataRepositoryService.send(
        presignRequest.method,
        presignRequest.path,
        presignRequest.data,
        presignRequest.authed,
        presignRequest.hasResponse,
        presignRequest.url,
        presignRequest.alterHeaders,
      );
    } catch (e) {
      throw toCabinetFileError("presign", e, "fileUploadFailed");
    }

    if (presignResponse?.uploadUrl) {
      let uploadResponse: Response;
      try {
        uploadResponse = await fetch(presignResponse.uploadUrl, {
          method: "PUT",
          body: encryptedBlob,
          headers: { "Content-Type": "text/plain" },
        });
      } catch (e) {
        // fetch() only rejects when no response arrived at all: offline, or blocked by the
        // page's CSP / the bucket's CORS (the browser console names which).
        throw new CabinetFileError("upload", "cabinetStorageUnreachable", undefined, undefined, e);
      }
      if (!uploadResponse.ok) {
        throw new CabinetFileError("upload", "fileUploadFailed", uploadResponse.status);
      }
      return true;
    }

    // IS_OFFLINE fallback: uploadUrl is null, use legacy POST path
    const encryptedFile = new File([encryptedBlob], vaultFile.id, { type: "text/plain" });
    const formData = new FormData();
    formData.append("file", encryptedFile);
    const uploadRequest = new GlossApiProxyRequest({ endpoint: "uploadFile", formData });
    let uploadResult;
    try {
      uploadResult = await this.dataRepositoryService.send(
        uploadRequest.method,
        uploadRequest.path,
        uploadRequest.data,
        uploadRequest.authed,
        uploadRequest.hasResponse,
        uploadRequest.url,
        uploadRequest.alterHeaders,
      );
    } catch (e) {
      throw toCabinetFileError("upload", e, "fileUploadFailed");
    }
    if (!uploadResult) {
      throw new CabinetFileError("upload", "fileUploadFailed");
    }
    return true;
  }

  /** Rehydrates the JSON key stored on a VaultFileView into a usable SymmetricCryptoKey. */
  private resolveEncryptionKey(encryptedKey: VaultFileView["encryptedKey"]): SymmetricCryptoKey {
    return SymmetricCryptoKey.fromJSON(encryptedKey);
  }

  /** Downloads the encrypted file content, preferring a presigned S3 GET (no size limit)
   * over the legacy proxied route, which is capped by the API Gateway payload limit. */
  private async fetchEncryptedFileText(fileId: string): Promise<string> {
    const presignRequest = new GlossApiProxyRequest({ endpoint: "presignDownloadFile", fileId });
    let presignResponse;
    try {
      presignResponse = await this.dataRepositoryService.send(
        presignRequest.method,
        presignRequest.path,
        presignRequest.data,
        presignRequest.authed,
        presignRequest.hasResponse,
        presignRequest.url,
        presignRequest.alterHeaders,
      );
    } catch (e) {
      throw toCabinetFileError("presign", e, "fileDownloadFailed");
    }

    if (presignResponse?.downloadUrl) {
      let downloadResponse: Response;
      try {
        downloadResponse = await fetch(presignResponse.downloadUrl);
      } catch (e) {
        // Same as the upload: no response at all means offline or blocked by CSP/CORS.
        throw new CabinetFileError(
          "download",
          "cabinetStorageUnreachable",
          undefined,
          undefined,
          e,
        );
      }
      if (!downloadResponse.ok) {
        throw new CabinetFileError("download", "fileDownloadFailed", downloadResponse.status);
      }
      return downloadResponse.text();
    }

    // IS_OFFLINE fallback: downloadUrl is null, use legacy proxied GET path
    const legacyRequest = new GlossApiProxyRequest({ endpoint: "downloadFile", fileId });
    try {
      const response = await this.dataRepositoryService.send(
        legacyRequest.method,
        legacyRequest.path,
        legacyRequest.data,
        legacyRequest.authed,
        legacyRequest.hasResponse,
        legacyRequest.url,
        legacyRequest.alterHeaders,
        true,
      );
      return await response.text();
    } catch (e) {
      throw toCabinetFileError("download", e, "fileDownloadFailed");
    }
  }

  private async decryptVaultFile(
    fileId: string,
  ): Promise<{ vaultFile: VaultFileView; file: File }> {
    const vaultFile = this.userStore.vaultFiles
      .vaultFileViews()
      .find((vaultFile) => vaultFile.id == fileId);
    const symmetricCryptoKey = this.resolveEncryptionKey(vaultFile.encryptedKey);

    const encB64Text = await this.fetchEncryptedFileText(fileId);
    const encText = Utils.fromB64ToArray(encB64Text).buffer;
    const encArrayBuffer = new EncArrayBuffer(encText);
    const file = await this.fileEncryption.decrypt(encArrayBuffer, symmetricCryptoKey);

    return { vaultFile, file };
  }

  /** Decrypted file for in-app preview, with its real name/mimeType restored. */
  async getFile(fileId: string): Promise<File> {
    const { vaultFile, file } = await this.decryptVaultFile(fileId);
    return new File([file], vaultFile.name, { type: vaultFile.mimeType });
  }

  /**
   * Replaces the content of an existing vault file, keeping its metadata/links intact.
   *
   * Re-encrypts under the file's EXISTING key (never generates a new one here). The two
   * writes below - S3 content, then vault metadata - are not atomic; if the vault write
   * fails after S3 succeeds, reusing the same key means the S3 content is still
   * decryptable with the key already stored in the vault. Only mimeType/fileSize would be
   * stale in that case, not an unrecoverable ciphertext (see gloss-client#3699).
   *
   * The encrypted buffer is threaded through as a value (encryptWith's return -> the
   * saveContentToServer parameter) rather than read back from FileEncryptionService's shared
   * instance state, so a concurrent encrypt()/encryptWith() call elsewhere (a new upload, or a
   * replace on a different file) can't overwrite the bytes this call is about to upload
   * (see gloss-client#3700).
   */
  async replaceFile(vaultFile: VaultFileView, file: File): Promise<boolean> {
    const existingKey = this.resolveEncryptionKey(vaultFile.encryptedKey);
    const encArrayBuffer = await this.fileEncryption.encryptWith(file, existingKey);

    try {
      await this.saveContentToServer(vaultFile, file, encArrayBuffer);
    } catch (e) {
      this.displayCatchError(e);
      return false;
    }

    vaultFile.mimeType = file.type as CabinetFileType;
    vaultFile.fileSize = file.size;

    await this.userStore.vaultFiles.save(vaultFile);
    this.globalService.showSuccessMessage("success", "fileUploadedSuccessfully");
    return true;
  }

  async deleteFile(vaultFile: VaultFileView, silent = false): Promise<void> {
    const data = { endpoint: "deleteFile", fileId: vaultFile.id };
    const glossRequestObject = new GlossApiProxyRequest(data);
    const response = await this.dataRepositoryService.send(
      glossRequestObject.method,
      glossRequestObject.path,
      glossRequestObject.data,
      glossRequestObject.authed,
      glossRequestObject.hasResponse,
      glossRequestObject.url,
      glossRequestObject.alterHeaders,
    );
    if (response) {
      await this.userStore.vaultFiles.delete(vaultFile);
      if (!silent) {
        this.globalService.showSuccessMessage("success", "fileDeletedSuccessfully");
      }
    }
  }
}
